import styled, { css } from 'styled-components';

import type { BoxProps } from '@/components/Box';
import { theme } from '@/theme/getTheme';

import BreitlingWhiteLogoSVG from '@/assets/breitling-logo-white.svg';

import * as S from './Loader.styles';
export type LoaderSize = 'xs' | 'sm' | 'md' | 'lg';
export type LoaderVariant = 'primary' | 'contrast' | 'primaryLight';
type FullHeight = 'fullHeight';

const getColor = (variant: LoaderVariant) => {
  switch (variant) {
    case 'contrast':
      return {
        background: theme.colors.white,
        color: theme.colors.navyBlueSub2,
      };

    case 'primary':
      return {
        background: theme.colors.primary,
        color: theme.colors.lightGrey,
      };
    case 'primaryLight':
      return {
        background: theme.colors.primaryLight,
        color: theme.colors.lightGrey,
      };
  }
};

const getSize = (size: LoaderSize) => {
  const mapping: Record<LoaderSize, number> = {
    xs: 20,
    sm: 32,
    md: 48,
    lg: 64,
  };
  return mapping[size];
};

const StyledLoader = styled.div<{
  size: LoaderSize;
  thickness: number;
  variant: LoaderVariant;
}>`
  display: inline-block;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  position: relative;

  ${({ size, thickness, variant }) => {
    const { background, color } = getColor(variant);
    return css`
      width: ${getSize(size)}px;
      height: ${getSize(size)}px;
      border-radius: 100%;
      border: ${thickness}px solid ${background};
      border-top-color: ${color};

      ${variant === 'contrast' &&
      css`
        &::after {
          content: '';
          background: url(${BreitlingWhiteLogoSVG}) no-repeat center center;
          background-size: contain;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%); rotate(-1s linear infinite);
          width: 50%;
          height: 50%;
          animation: counter-spin 1s linear infinite;
        }
      `}
    `;
  }};

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes counter-spin {
    to {
      transform: translate(-50%, -50%) rotate(-360deg);
    }
  }
`;
export interface LoaderProps extends BoxProps {
  className?: string;
  height?: number | FullHeight;
  noMargin?: boolean;
  size?: LoaderSize;
  thickness?: number;
  variant?: LoaderVariant;
  // loader height below size medium
  verticalLoaderHeight?: number | FullHeight | '100vw';
  width?: number;
}

export const Loader = ({
  className,
  height = 200,
  size = 'md',
  thickness = 3,
  variant = 'primary',
  verticalLoaderHeight,
  width,
  ...props
}: LoaderProps) => (
  <S.Wrapper
    className={className}
    data-test-id='LoaderWrapper'
    data-testid='loader'
    height={height}
    verticalLoaderHeight={verticalLoaderHeight}
    width={width}
    {...props}
  >
    <StyledLoader
      data-test-id='LoaderStyledLoader'
      size={size}
      thickness={thickness}
      variant={variant}
    />
  </S.Wrapper>
);
